import { useState } from 'react';
import { Box, Button, Card, TextField, Typography } from '@mui/material';
import {
  Datagrid,
  Filter,
  FunctionField,
  List,
  SelectInput,
  TextInput,
  useNotify,
  useRefresh,
  useTranslate,
} from 'react-admin';
import { ConfirmDialog } from 'src/components/custom-dialog';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { DashboardContent } from 'src/layouts/dashboard';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import { paths } from 'src/routes/paths';
import ErrorPage403 from 'src/pages/error/403';
import {
  stErrorMessage,
  StPoolEnrollment,
  stReleasePoolEnrollment,
} from '../../../services/socialTradingService';
import { Resources } from '../../resource';
import {
  StActionButton,
  StActionRow,
  StIdCell,
  StStatusChip,
  stFormatDateTime,
  stText,
} from '../StListUi';
import './init';

const ListFilter = (props: any) => {
  const t = useTranslate();
  return (
    <Filter {...(props || {})}>
      <TextInput source="poolId" label={t('module.socialTrading.colPoolId')} alwaysOn resettable />
      <TextInput source="roomId" label={t('module.socialTrading.colRoomId')} alwaysOn resettable />
      <TextInput
        source="roomName"
        label={t('module.socialTrading.colRoomName')}
        alwaysOn
        resettable
      />
      <TextInput source="userId" label={t('module.socialTrading.colUserId')} alwaysOn resettable />
      <SelectInput
        source="status"
        label={t('module.socialTrading.colStatus')}
        choices={[
          { id: 'ACTIVE', name: 'ACTIVE' },
          { id: 'ENDED', name: 'ENDED' },
          { id: 'RELEASED', name: 'RELEASED' },
        ]}
        alwaysOn
        resettable
      />
    </Filter>
  );
};

export default function SocialTradingPoolEnrollmentsList() {
  const t = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [releaseTarget, setReleaseTarget] = useState<StPoolEnrollment | null>(null);
  const [reason, setReason] = useState('');
  const [releasing, setReleasing] = useState(false);

  const openRelease = (row: StPoolEnrollment) => {
    setReleaseTarget(row);
    setReason('');
  };

  const closeRelease = () => {
    if (releasing) return;
    setReleaseTarget(null);
    setReason('');
  };

  const confirmRelease = async () => {
    if (!releaseTarget) return;
    const trimmed = reason.trim();
    if (!trimmed) {
      notify(t('module.socialTrading.releaseEnrollmentNeedReason'), { type: 'warning' });
      return;
    }
    setReleasing(true);
    try {
      await stReleasePoolEnrollment(releaseTarget.id, trimmed);
      notify(t('module.socialTrading.saveOk'), { type: 'success' });
      setReleaseTarget(null);
      setReason('');
      refresh();
    } catch (e: any) {
      notify(stErrorMessage(e), { type: 'error' });
    } finally {
      setReleasing(false);
    }
  };

  return (
    <PermissionGuard resource="st-pool-enrollments" action="read" fallback={<ErrorPage403 />}>
      <DashboardContent maxWidth={false}>
        <CustomBreadcrumbs
          heading={t('module.socialTrading.poolEnrollments')}
          links={[
            { name: t('module.socialTrading.title'), href: paths.socialTrading.workbench.root },
            { name: t('module.socialTrading.poolEnrollments') },
          ]}
        />

        <Card sx={{ mt: 2, pb: 2 }}>
          <List
            resource={Resources.socialTradingPoolEnrollments}
            actions={false}
            empty={false}
            perPage={20}
            exporter={false}
            filters={<ListFilter />}
            sx={{
              '& .RaFilter-form': {
                px: 2,
                pt: 2,
                pb: 1,
                columnGap: 2,
                rowGap: 1.5,
                alignItems: 'flex-end',
              },
            }}
          >
            <Box sx={{ overflow: 'auto' }}>
              <Datagrid
                rowClick={false}
                bulkActionButtons={false}
                empty={
                  <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                    {t('module.socialTrading.emptyEnrollments')}
                  </Typography>
                }
              >
                <FunctionField
                  label={t('module.socialTrading.colPoolId')}
                  render={(row: StPoolEnrollment) => <StIdCell value={row.poolId} />}
                />
                <FunctionField
                  label={t('module.socialTrading.colRoom')}
                  render={(row: StPoolEnrollment) => (
                    <Box>
                      <Typography variant="body2">{stText(row.roomName)}</Typography>
                      <StIdCell value={row.roomId} />
                    </Box>
                  )}
                />
                <FunctionField
                  label={t('module.socialTrading.colUserId')}
                  render={(row: StPoolEnrollment) => <StIdCell value={row.userId} />}
                />
                <FunctionField
                  label={t('module.socialTrading.colEnrolledAt')}
                  render={(row: StPoolEnrollment) => stText(stFormatDateTime(row.enrolledAt))}
                />
                <FunctionField
                  label={t('module.socialTrading.colStatus')}
                  render={(row: StPoolEnrollment) => <StStatusChip status={row.status} />}
                />
                <FunctionField
                  label={t('module.socialTrading.colCycleStart')}
                  render={(row: StPoolEnrollment) => stText(stFormatDateTime(row.cycleStart))}
                />
                <FunctionField
                  label={t('module.socialTrading.colCycleEnd')}
                  render={(row: StPoolEnrollment) => stText(stFormatDateTime(row.cycleEnd))}
                />
                <FunctionField
                  label={t('module.socialTrading.actions')}
                  textAlign="right"
                  render={(row: StPoolEnrollment) =>
                    row.status === 'ACTIVE' ? (
                      <StActionRow>
                        <StActionButton color="warning" onClick={() => openRelease(row)}>
                          {t('module.socialTrading.releaseEnrollment')}
                        </StActionButton>
                      </StActionRow>
                    ) : null
                  }
                />
              </Datagrid>
            </Box>
          </List>
        </Card>

        <ConfirmDialog
          open={!!releaseTarget}
          onClose={closeRelease}
          title={t('module.socialTrading.confirmReleaseEnrollment')}
          content={
            releaseTarget ? (
              <>
                <Typography variant="body2">
                  {t('module.socialTrading.colUserId')}: {releaseTarget.userId}
                  <br />
                  <Typography component="span" variant="caption">
                    {releaseTarget.id}
                  </Typography>
                </Typography>
                <Typography variant="body2" sx={{ mt: 1, mb: 1.5 }}>
                  {t('module.socialTrading.confirmReleaseEnrollmentBody')}
                </Typography>
                <TextField
                  size="small"
                  fullWidth
                  required
                  multiline
                  minRows={2}
                  label={t('module.socialTrading.releaseEnrollmentReason')}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  disabled={releasing}
                />
              </>
            ) : null
          }
          action={
            <Button
              variant="contained"
              color="error"
              disabled={releasing || !reason.trim()}
              onClick={confirmRelease}
            >
              {t('module.socialTrading.releaseEnrollment')}
            </Button>
          }
        />
      </DashboardContent>
    </PermissionGuard>
  );
}
